import React, { createContext, useMemo, useContext, useEffect, useState } from "react";
import { useAuth } from "./useAuth";
import { useToast } from "@chakra-ui/react";

interface Group {
  isRoot: boolean;
  groups: string[];
  selected: string;
  changeGroup: (p: string) => void;
  canAccessGroup: (groupName: string) => boolean;
}

export const GroupContext = createContext<Group>({
  isRoot: false,
  groups: [],
  selected: "",
  changeGroup: (p: string) => { },
  canAccessGroup: (groupName: string) => false,
});

export const GroupProvider = ({ children }: { children: React.ReactElement | React.ReactElement[] }): React.ReactElement => {
  const toast = useToast();
  const { user } = useAuth();

  const [selected, setSelected] = useState<string>(() => {
    const savedGroup = sessionStorage.getItem('selectedGroup');
    // WaitGroup은 유효한 선택이 아니므로 초기값에서 제외
    if (savedGroup && savedGroup !== "WaitGroup") {
      return savedGroup;
    }
    return "";
  });

  useEffect(() => {
    if (!user || !user.Groups || user.Groups.length === 0) {
      return;
    }

    const savedGroup = sessionStorage.getItem('selectedGroup');

    // 저장된 그룹이 WaitGroup이면 무효 처리 (권한 부여 후 잔존 방지)
    if (savedGroup === "WaitGroup") {
      sessionStorage.removeItem('selectedGroup');
    }

    // 저장된 그룹이 유효하고 접근 가능하면 사용
    if (savedGroup && savedGroup !== "WaitGroup" && user.Groups.includes(savedGroup)) {
      if (selected !== savedGroup) {
        setSelected(savedGroup);
      }
      return;
    }

    // 현재 선택된 그룹이 유효하지 않은 경우 첫 번째 그룹으로 변경
    if (!selected || !user.Groups.includes(selected)) {
      setSelected(user.Groups[0]);
      sessionStorage.setItem('selectedGroup', user.Groups[0]);
    }
  }, [user]);

  // WaitGroup 메시지를 별도 useEffect로 분리
  useEffect(() => {
    if (user && user.Groups && user.Groups.length === 1 && user.Groups[0] === "WaitGroup") {
      toast({
        title: "계정 승인 대기 중",
        description: "계정이 승인 대기 중입니다. 관리자가 그룹을 할당할 때까지 기다려주세요.",
        status: "warning",
        isClosable: true,
        duration: 15000,
      });
    }
  }, [user?.Groups, toast]);

  const canAccessGroup = (groupName: string): boolean => {
    if (!user) return false;
    if (user.isRoot) return true; // 관리자는 모든 그룹 접근 가능
    return user.Groups.includes(groupName);
  };

  const value = useMemo<Group>(
    () => {
      const rootValue = user?.isRoot || false;
      return {
        isRoot: rootValue,
        groups: user?.Groups || [],
        selected: selected,
        canAccessGroup,
        changeGroup: (g) => {
          // 로그아웃 처리
          if (g === "" || g === null) {
            setSelected("");
            sessionStorage.removeItem('selectedGroup');
            return;
          }

          if (!canAccessGroup(g)) {
            toast({
              title: "접근 권한 없음",
              description: "해당 그룹에 대한 접근 권한이 없습니다.",
              status: "error",
              isClosable: true,
              duration: 5000,
            });
            return;
          }

          if (g === "WaitGroup") {
            toast({
              title: "안내",
              description: "계정이 승인 대기 중입니다. 관리자가 적절한 그룹을 할당할 때까지 기다려주세요.",
              status: "warning",
              isClosable: true,
              duration: 10000,
            });
          }
          setSelected(g);
          sessionStorage.setItem('selectedGroup', g);
        },
      };
    },
    [selected, user]
  );

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
};

export const useGroup = (): Group => {
  return useContext(GroupContext);
};
